/**
 * 🚌 Event Bus typé et centralisé avec métriques
 * 
 * Remplace les window.dispatchEvent éparpillés dans le code
 * Avantages :
 * - Types stricts pour tous les événements
 * - Un seul point de communication
 * - Facilite le debugging avec logs optionnels
 * - Prévient les erreurs de typage
 * - Métriques et analytics intégrés
 */

import { logger } from '@/services/logger';

/**
 * Définition de tous les événements de l'application
 * Ajouter un événement ici pour le rendre disponible dans tout le code
 */
export interface EventMap {
  // === Streak Events ===
  'streak:updated': { userId: string; newStreak: number; previousStreak?: number };
  'streak:broken': { userId: string; previousStreak: number };
  'streak:repaired': { userId: string; newStreak: number };
  'streak:restored': { userId: string; restoredStreak: number; gelsUsed: number };
  'streak:protection-applied': { userId: string; protectionDays: number };
  'streak:saved': { userId: string; streakValue: number; freezeName: string; protectionDays: number };
  
  // === Progression Events ===
  'progression:updated': { userId: string; xpEarned?: number; multiplier?: number; bonusType?: 'streak' | 'boost' | 'combo' };
  
  // === Goal Events ===
  'goal:completed': { goalId: string; userId: string; points: number; xp: number };
  'goal:uncompleted': { goalId: string; userId: string };
  
  // === Validation Events ===
  'validation:failed': { goalId: string; reason?: string };
  // Émis quand l'utilisateur choisit, depuis l'écran d'échec Photo IA,
  // de basculer vers une validation par un collègue (P2P).
  'validation:switch-to-p2p': { goalId: string; goalTitle: string };
  
  // === Participation Events ===
  'participations:updated': void;
  
  // === Challenge Events ===
  'challenge:joined': { challengeId: string; userId: string; challengeType?: string };
  'challenge:left': { challengeId: string; userId: string };
  'challenge:completed': { challengeId: string; userId: string };
  
  // === Boost Events ===
  'boost:activated': { boostId: string; userId: string; multiplier: number };
  'boost:expired': { boostId: string };
  'boost:stats-updated': { id: string; accumulatedXP?: number; activitiesCompleted?: number };
  
  // === Employee Events ===
  'employee:updated': { employeeId: string; updates: Record<string, unknown> };
  'employee:status-changed': { employeeId: string; previousStatus?: string };
  'employee:data-changed': void;
  
  // === Profile Events ===
  'profile:updated': { userId: string; field?: string };
  
  // === Ranking Events ===
  'ranking:period-changed': void;
  
  // === Department/Team CRUD Events ===
  'department:created': { departmentId: string };
  'department:added': { department: { id: string; name: string; organizationId: string; parentDepartmentId?: string; managerId?: string; createdAt: string } };
  'department:updated': { departmentId: string; updates: Partial<{ id: string; name: string; organizationId: string; parentDepartmentId?: string; managerId?: string; createdAt: string }> };
  'department:deleted': { departmentId: string };
  'team:created': { teamId: string };
  'team:added': { team: { id: string; name: string; departmentId: string; leaderId?: string; createdAt: string } };
  'team:updated': { teamId: string; updates: Partial<{ id: string; name: string; departmentId: string; leaderId?: string; createdAt: string }> };
  'team:deleted': { teamId: string };
  
  // === Manager Events ===
  'manager:notifications-updated': void;
  'reward-claims:updated': void;
  'company-rewards:updated': void;
  
  // === Tour/Onboarding Events ===
  'tour:started': void;
  'tour:ended': void;
  'tour:action-completed': { actionType: string };
  'onboarding:updated': { step?: string; completed?: boolean };
  
  // === Team Events ===
  'team:join-requests-updated': void;
  'team:invitations-updated': void;
  'challenge:enrollment-requests-updated': void;
  'team:member-added': { teamId: string; userId: string };
  'team:member-removed': { teamId: string; userId: string };
  'team:dissolved': { teamId: string; captainId: string };
  
  // === Reward Events ===
  'reward:claimed': { rewardId: string; userId: string; diamonds: number };
  'reward:unlocked': { rewardId: string; userId: string };
  'reward:claim-created': { claim: Record<string, unknown> };
  
  // === Session Events ===
  'session:invalid': void;
  'consent:revoked': void;
  'consent:changed': { choice: 'accepted' | 'refused' };
  'consent:saved': { userId: string; role: string };
  
  // === Sync Events ===
  'sync:required': { tables: string[] };
  'storage:changed': { key: string; newValue?: unknown };
  'goals:reload': { reason: string; goalId?: string };
  
  // === Animation Events ===
  'animation:mode-changed': { mode: 'full' | 'reduced' | 'off' };
  
  // === Toast Events ===
  'show-toast': { type?: string; title: string; message?: string; actionUrl?: string; showBanner?: boolean };
  
  // === CO2 Badge Events ===
  'co2:major-badge-unlocked': { badge: { id: string; name: string; description: string; icon: string; bonusPoints: number }; co2SavedKg: number };
  
  // === Badge Unlock Events (all badges) ===
  'badge:unlocked': { badge: { id: string; name: string; description: string; icon: string; bonusPoints: number; category?: string } };
  'badges:bundle-unlocked': { badges: Array<{ id: string; name: string; description: string; icon: string; bonusPoints: number; category?: string }> };
  
  // === Avatar Unlock Events ===
  'avatar:unlocked': { avatar: { id: string; name: string; description: string; color: string; isPremium: boolean; requiredLevel: number; lottieSrc: string | null } };
  
  // === Level Up Events ===
  'level:up': { userId: string; previousLevel: number; newLevel: number };
  
  // === Celebration Coordination Events ===
  'celebration:active': { source: string };
  'celebration:idle': { source: string };
  
  // === Reading Stats Events ===
  'reading:validated': { 
    goalId: string; 
    goalTitle?: string; 
    suggestedBookTitle?: string | null;
    suggestedBookAuthor?: string | null;
  };

  // === Partner Provider Events ===
  'partner:status-changed': { providerId: string; newStatus: string; updatedBy?: string };

  // === App Review Incentive Events ===
  'trigger-review-incentive': void;

  // === Realtime Rank Events ===
  'rank:changed': {
    scope: 'company' | 'global';
    from: number;
    to: number;
    delta: number; // positive = went up (smaller rank number), negative = went down
  };
  'team-rank:changed': {
    teamId: string;
    challengeId: string;
    from: number;
    to: number;
    delta: number;
  };
}

// Types exportés pour réutilisation
export type EventKey = keyof EventMap;
export type EventHandler<K extends EventKey> = EventMap[K] extends void 
  ? () => void 
  : (data: EventMap[K]) => void;

// ==================== Types Middleware ====================

/**
 * Contexte passé aux middlewares
 */
export interface MiddlewareContext<K extends EventKey = EventKey> {
  event: K;
  data: EventMap[K];
  timestamp: number;
  /** Permet au middleware de stopper la propagation */
  stopPropagation: () => void;
  /** Permet au middleware de modifier les données */
  setData: (newData: EventMap[K]) => void;
  /** Métadonnées custom ajoutées par les middlewares */
  meta: Record<string, unknown>;
}

/**
 * Signature d'un middleware
 */
export type EventMiddleware = (
  context: MiddlewareContext<any>,
  next: () => void
) => void;

/**
 * Options pour ajouter un middleware
 */
export interface MiddlewareOptions {
  /** Priorité (plus bas = exécuté en premier) */
  priority?: number;
  /** Filtre sur certains événements seulement */
  events?: EventKey[];
}

/**
 * Métriques pour un événement spécifique
 */
export interface EventMetrics {
  emitCount: number;
  lastEmitAt: number | null;
  listenerCount: number;
  totalListenersAdded: number;
  avgHandlerDuration: number;
  totalHandlerDuration: number;
  errors: number;
}

/**
 * Métriques globales du bus d'événements
 */
export interface GlobalMetrics {
  totalEmits: number;
  totalErrors: number;
  startTime: number;
  peakListenerCount: number;
  currentListenerCount: number;
}

/**
 * Crée des métriques vides pour un événement
 */
function createEmptyMetrics(): EventMetrics {
  return {
    emitCount: 0,
    lastEmitAt: null,
    listenerCount: 0,
    totalListenersAdded: 0,
    avgHandlerDuration: 0,
    totalHandlerDuration: 0,
    errors: 0,
  };
}

/**
 * Bus d'événements typé avec métriques et middlewares intégrés
 */
class TypedEventBus {
  private handlers: Map<EventKey, Set<EventHandler<any>>> = new Map();
  private debugMode = false;
  
  // Système de métriques
  private metricsEnabled = false;
  private eventMetrics: Map<EventKey, EventMetrics> = new Map();
  private globalMetrics: GlobalMetrics = {
    totalEmits: 0,
    totalErrors: 0,
    startTime: Date.now(),
    peakListenerCount: 0,
    currentListenerCount: 0,
  };

  // Système de middlewares
  private middlewares: Array<{
    fn: EventMiddleware;
    priority: number;
    events: EventKey[] | null;
  }> = [];

  /**
   * Active/désactive le mode debug (logs tous les événements)
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    if (enabled) {
      logger.info('[EventBus] Debug mode enabled');
    }
  }

  /**
   * Active le système de métriques
   */
  enableMetrics(): void {
    this.metricsEnabled = true;
    this.globalMetrics.startTime = Date.now();
    logger.info('[EventBus] Metrics enabled');
  }

  /**
   * Désactive le système de métriques
   */
  disableMetrics(): void {
    this.metricsEnabled = false;
    logger.info('[EventBus] Metrics disabled');
  }

  /**
   * Vérifie si les métriques sont activées
   */
  isMetricsEnabled(): boolean {
    return this.metricsEnabled;
  }

  /**
   * Récupère ou crée les métriques pour un événement
   */
  private getOrCreateMetrics(event: EventKey): EventMetrics {
    if (!this.eventMetrics.has(event)) {
      this.eventMetrics.set(event, createEmptyMetrics());
    }
    return this.eventMetrics.get(event)!;
  }

  /**
   * Met à jour le compteur de listeners
   */
  private updateListenerCount(): void {
    let total = 0;
    this.handlers.forEach(set => {
      total += set.size;
    });
    this.globalMetrics.currentListenerCount = total;
    if (total > this.globalMetrics.peakListenerCount) {
      this.globalMetrics.peakListenerCount = total;
    }
  }

  // ==================== API Middlewares ====================

  /**
   * Ajoute un middleware à la chaîne
   */
  use(middleware: EventMiddleware, options?: MiddlewareOptions): () => void {
    const entry = {
      fn: middleware,
      priority: options?.priority ?? 100,
      events: options?.events ?? null,
    };
    
    this.middlewares.push(entry);
    // Trier par priorité (plus bas = exécuté en premier)
    this.middlewares.sort((a, b) => a.priority - b.priority);
    
    // Retourne une fonction de cleanup
    return () => {
      const idx = this.middlewares.indexOf(entry);
      if (idx !== -1) this.middlewares.splice(idx, 1);
    };
  }

  /**
   * Supprime tous les middlewares
   */
  clearMiddlewares(): void {
    this.middlewares = [];
  }

  /**
   * Récupère le nombre de middlewares actifs
   */
  getMiddlewareCount(): number {
    return this.middlewares.length;
  }

  /**
   * Exécute la chaîne de middlewares de manière synchrone
   */
  private runMiddlewares<K extends EventKey>(
    event: K,
    data: EventMap[K]
  ): { proceed: boolean; finalData: EventMap[K] } {
    let stopped = false;
    let currentData = data;
    
    const context: MiddlewareContext<K> = {
      event,
      data: currentData,
      timestamp: Date.now(),
      stopPropagation: () => { stopped = true; },
      setData: (newData) => { 
        currentData = newData;
        context.data = newData;
      },
      meta: {},
    };

    // Filtrer les middlewares applicables à cet événement
    const applicableMiddlewares = this.middlewares.filter(
      m => m.events === null || m.events.includes(event)
    );

    // Exécuter la chaîne
    for (const mw of applicableMiddlewares) {
      if (stopped) break;
      
      let nextCalled = false;
      mw.fn(context, () => { nextCalled = true; });
      
      // Si next() n'est pas appelé, on stoppe
      if (!nextCalled) {
        stopped = true;
        break;
      }
    }

    return { proceed: !stopped, finalData: currentData };
  }

  /**
   * Émet un événement avec des données typées
   */
  emit<K extends EventKey>(
    event: K, 
    ...[data]: EventMap[K] extends void ? [] : [EventMap[K]]
  ): void {
    // Exécuter les middlewares d'abord
    const { proceed, finalData } = this.runMiddlewares(event, data as EventMap[K]);
    
    if (!proceed) {
      if (this.debugMode) {
        logger.info(`[EventBus] Event ${event} blocked by middleware`);
      }
      return;
    }

    if (this.debugMode) {
      logger.info(`[EventBus] Emit: ${event}`, finalData);
    }

    const startTime = this.metricsEnabled ? performance.now() : 0;
    let handlerErrors = 0;

    const eventHandlers = this.handlers.get(event);
    if (eventHandlers) {
      eventHandlers.forEach(handler => {
        try {
          if (finalData !== undefined) {
            (handler as (data: EventMap[K]) => void)(finalData);
          } else {
            (handler as () => void)();
          }
        } catch (error) {
          logger.error(`[EventBus] Error in handler for ${event}:`, error);
          handlerErrors++;
        }
      });
    }

    // Mise à jour des métriques
    if (this.metricsEnabled) {
      const duration = performance.now() - startTime;
      const metrics = this.getOrCreateMetrics(event);
      
      metrics.emitCount++;
      metrics.lastEmitAt = Date.now();
      metrics.errors += handlerErrors;
      metrics.totalHandlerDuration += duration;
      metrics.avgHandlerDuration = metrics.totalHandlerDuration / metrics.emitCount;
      
      this.globalMetrics.totalEmits++;
      this.globalMetrics.totalErrors += handlerErrors;
    }
  }

  /**
   * Écoute un événement
   */
  on<K extends EventKey>(event: K, handler: EventHandler<K>): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Mise à jour des métriques
    if (this.metricsEnabled) {
      const metrics = this.getOrCreateMetrics(event);
      metrics.listenerCount++;
      metrics.totalListenersAdded++;
      this.updateListenerCount();
    }

    // Retourne une fonction de cleanup
    return () => this.off(event, handler);
  }

  /**
   * Écoute un événement une seule fois
   */
  once<K extends EventKey>(event: K, handler: EventHandler<K>): () => void {
    const wrappedHandler = ((...args: any[]) => {
      this.off(event, wrappedHandler as EventHandler<K>);
      (handler as Function)(...args);
    }) as EventHandler<K>;

    return this.on(event, wrappedHandler);
  }

  /**
   * Supprime un listener
   */
  off<K extends EventKey>(event: K, handler: EventHandler<K>): void {
    const eventHandlers = this.handlers.get(event);
    if (eventHandlers) {
      eventHandlers.delete(handler);
      
      // Mise à jour des métriques
      if (this.metricsEnabled) {
        const metrics = this.getOrCreateMetrics(event);
        metrics.listenerCount = Math.max(0, metrics.listenerCount - 1);
        this.updateListenerCount();
      }
    }
  }

  /**
   * Supprime tous les listeners d'un événement
   */
  removeAllListeners<K extends EventKey>(event: K): void {
    const eventHandlers = this.handlers.get(event);
    if (eventHandlers && this.metricsEnabled) {
      const metrics = this.getOrCreateMetrics(event);
      metrics.listenerCount = 0;
    }
    this.handlers.delete(event);
    if (this.metricsEnabled) {
      this.updateListenerCount();
    }
  }

  // ==================== API Métriques ====================

  /**
   * Récupère les métriques d'un événement spécifique
   */
  getEventMetrics<K extends EventKey>(event: K): EventMetrics | undefined {
    return this.eventMetrics.get(event);
  }

  /**
   * Récupère toutes les métriques par événement
   */
  getAllMetrics(): Record<string, EventMetrics> {
    const result: Record<string, EventMetrics> = {};
    this.eventMetrics.forEach((metrics, event) => {
      result[event] = { ...metrics };
    });
    return result;
  }

  /**
   * Récupère les métriques globales
   */
  getGlobalMetrics(): GlobalMetrics {
    return { ...this.globalMetrics };
  }

  /**
   * Récupère les événements les plus actifs
   */
  getMostActiveEvents(limit = 10): Array<{ event: EventKey; count: number; avgDuration: number }> {
    const events: Array<{ event: EventKey; count: number; avgDuration: number }> = [];
    
    this.eventMetrics.forEach((metrics, event) => {
      if (metrics.emitCount > 0) {
        events.push({
          event,
          count: metrics.emitCount,
          avgDuration: metrics.avgHandlerDuration,
        });
      }
    });
    
    return events
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Récupère les événements déclarés mais jamais émis
   */
  getUnusedEvents(): EventKey[] {
    const allEvents = Object.keys({} as EventMap) as EventKey[];
    const usedEvents = new Set<EventKey>();
    
    this.eventMetrics.forEach((metrics, event) => {
      if (metrics.emitCount > 0) {
        usedEvents.add(event);
      }
    });
    
    // Retourne les événements avec des listeners mais jamais émis
    const unusedWithListeners: EventKey[] = [];
    this.handlers.forEach((handlers, event) => {
      if (handlers.size > 0 && !usedEvents.has(event)) {
        unusedWithListeners.push(event);
      }
    });
    
    return unusedWithListeners;
  }

  /**
   * Récupère les événements avec des erreurs
   */
  getEventsWithErrors(): Array<{ event: EventKey; errors: number; errorRate: number }> {
    const events: Array<{ event: EventKey; errors: number; errorRate: number }> = [];
    
    this.eventMetrics.forEach((metrics, event) => {
      if (metrics.errors > 0) {
        events.push({
          event,
          errors: metrics.errors,
          errorRate: metrics.emitCount > 0 ? (metrics.errors / metrics.emitCount) * 100 : 0,
        });
      }
    });
    
    return events.sort((a, b) => b.errors - a.errors);
  }

  /**
   * Réinitialise toutes les métriques
   */
  resetMetrics(): void {
    this.eventMetrics.clear();
    this.globalMetrics = {
      totalEmits: 0,
      totalErrors: 0,
      startTime: Date.now(),
      peakListenerCount: this.globalMetrics.currentListenerCount,
      currentListenerCount: this.globalMetrics.currentListenerCount,
    };
    logger.info('[EventBus] Metrics reset');
  }

  /**
   * Exporte les métriques en JSON (pour debugging/logging)
   */
  exportMetrics(): string {
    return JSON.stringify({
      global: this.getGlobalMetrics(),
      events: this.getAllMetrics(),
      topEvents: this.getMostActiveEvents(10),
      errors: this.getEventsWithErrors(),
      uptime: Date.now() - this.globalMetrics.startTime,
    }, null, 2);
  }

  /**
   * Récupère un résumé des métriques pour le debugging rapide
   */
  getMetricsSummary(): {
    uptime: number;
    totalEmits: number;
    totalErrors: number;
    activeListeners: number;
    peakListeners: number;
    uniqueEventsEmitted: number;
  } {
    let uniqueEventsEmitted = 0;
    this.eventMetrics.forEach(metrics => {
      if (metrics.emitCount > 0) uniqueEventsEmitted++;
    });

    return {
      uptime: Date.now() - this.globalMetrics.startTime,
      totalEmits: this.globalMetrics.totalEmits,
      totalErrors: this.globalMetrics.totalErrors,
      activeListeners: this.globalMetrics.currentListenerCount,
      peakListeners: this.globalMetrics.peakListenerCount,
      uniqueEventsEmitted,
    };
  }
}

// Singleton exporté
export const eventBus = new TypedEventBus();

// Hook React pour utiliser le bus d'événements
export function useEventBus() {
  return eventBus;
}
